/**
 * Ky client setup — GENERATED ONCE, will NOT be overwritten on subsequent runs.
 * Re-generate intentionally with: swaggie ... --clientSetup <path> --forceSetup
 *
 * This file configures the ky HTTP client used by the generated API client.
 * Because ky requires hooks to be provided at creation time, this file exports
 * a `createKyConfig()` function that is called by `initKyHttp()` in api.ts.
 *
 * Usage in your app:
 *   import { initKyHttp } from './api';
 *   initKyHttp(); // call once at startup, e.g. in a React provider or main.ts
 *
 * If you need to pass runtime values into hooks (e.g. an auth token getter),
 * store them in module-level variables and expose a configuration function:
 *
 *   let _getToken: () => Promise<string> = () => Promise.resolve('');
 *
 *   export function configureKyClient(opts: { getToken: () => Promise<string> }) {
 *     _getToken = opts.getToken;
 *   }
 *
 * Then call configureKyClient(...) before initKyHttp() at startup.
 */
import { AuthRef } from '../nest/setup';

import * as Sentry from '@sentry/react';
import { StatusCodes } from 'http-status-codes';
import { isHTTPError } from 'ky';
import { CRIBL_AGENT_HEADER_NAME, MAESTRO_AGENT } from 'shared/src/observability/constants';

import type { Options as KyOptions } from 'ky';

export type KySetupConfig = Pick<KyOptions, 'hooks' | 'prefix' | 'retry' | 'timeout' | 'credentials'>;

let zeusAuthRef: AuthRef | null = null;

/**
 * Binds auth to ky hooks (via module ref). Call before `initKyHttp()`.
 * Returns cleanup that clears the ref (used on unmount).
 */
export function setupZeusV2Interceptors(authRef: AuthRef): () => void {
  zeusAuthRef = authRef;

  return () => {
    zeusAuthRef = null;
  };
}

/**
 * Returns the ky configuration used to create the HTTP client instance.
 * Called once by `initKyHttp()` in the generated api.ts.
 *
 * Add your hooks here — beforeRequest for auth/headers, afterResponse for
 * error handling and monitoring, beforeError for error enrichment.
 */
export function createKyConfig(): KySetupConfig {
  return {
    prefix: `${process.env.ZEUS_API_URL ?? ''}/v2/`,
    timeout: false,
    credentials: 'include',
    hooks: {
      beforeRequest: [
        async ({ request }) => {
          request.headers.set(CRIBL_AGENT_HEADER_NAME, MAESTRO_AGENT);

          const ref = zeusAuthRef?.current;

          if (!ref) {
            return;
          }

          const token = await ref.getAccessToken();

          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }
        },
      ],
      beforeError: [
        async ({ error, request }) => {
          if (!isHTTPError(error)) {
            return error;
          }

          const status = error.response.status;
          // error.data is pre-parsed by ky before beforeError hooks run (JSON responses only)
          const errorField =
            typeof error.data === 'object' && error.data !== null
              ? (error.data as Record<string, unknown>).error
              : undefined;

          if (
            status === StatusCodes.UNAUTHORIZED ||
            errorField === 'login_required' ||
            errorField === 'invalid_grant'
          ) {
            Sentry.captureException(error, {
              contexts: {
                api: {
                  name: 'AuthenticatedFetcher',
                  url: request.url,
                  status,
                  error: errorField,
                },
              },
            });
          }

          return error;
        },
      ],
    },
  };
}
